/**  @jsx jsx */

import { Fragment, ReactNode } from 'react';

import {
  Breadcrumbs,
  ButtonGroup,
  Grid,
  Link as ZinniaLink,
  Margin,
  Spacer,
  Text,
} from '@zapier/design-system';
import { jsx, css, Interpolation } from '@emotion/core';

import Link from 'app/common/components/Link';
import NonEditableUI from 'app/developer-v3/components/NonEditableUI';
import {
  useCurrentVersionReadOnlyReason,
  useRouter,
} from 'app/developer-v3/hooks';
import {
  isAdvancedIntegrationRoute,
  isGUIIntegrationOnlyRoute,
} from 'app/developer-v3/utils/routeKeyChecks';

import { LayoutStyles } from './constants';

const Styles = {
  root: ({ shouldLimitWidth }) => [
    css`
      padding: ${LayoutStyles.verticalPadding} ${LayoutStyles.horizontalPadding};
    `,
    shouldLimitWidth &&
      css`
        max-width: ${LayoutStyles.maxWidth};
      `,
  ],
};

type Breadcrumb = {
  text: string;
  url?: string;
};

type Props = {
  afterHeader?: ReactNode;
  afterRender?: ReactNode;
  breadcrumbs?: Breadcrumb[];
  buttonRoute: string;
  buttonText: string;
  children?: ReactNode;
  className?: Interpolation;
  description?: ReactNode;
  isButtonDisabled: boolean;
  renderDescription?: () => ReactNode;
  shouldLimitWidth: boolean;
  title?: string;
};

const ManagerLayout = ({
  afterHeader,
  afterRender,
  breadcrumbs,
  buttonRoute,
  buttonText,
  children,
  className,
  description,
  isButtonDisabled,
  renderDescription,
  shouldLimitWidth,
  title,
}: Props) => {
  const { routeKey } = useRouter();

  // The following hooks expect a router object with a 'current' property,
  // so we can just pass in a stubbed object with the expected properties to maintain
  // compatibility with other code using the hooks.
  const stubRouter = { current: { routeKey } };
  const isGUIOnlyRoute = isGUIIntegrationOnlyRoute(stubRouter);
  const isAdvancedRoute = isAdvancedIntegrationRoute(stubRouter);

  const currentVersionReadOnlyReason = useCurrentVersionReadOnlyReason();
  const isReadOnly = !!currentVersionReadOnlyReason;

  // The Advanced route renders an EditorLayout, which itself renders a NonEditableUI.
  // We don't want to render two NonEditableUI at once, so we only render the
  // NonEditableUI if we're on a GUI-only route but not on the Advanced route.
  const shouldRenderNonEditableUI =
    isReadOnly && !isAdvancedRoute && isGUIOnlyRoute;

  return (
    <main css={[Styles.root({ shouldLimitWidth }), className]}>
      {breadcrumbs && (
        <Fragment>
          <Breadcrumbs>
            {breadcrumbs.map(breadcrumb =>
              'url' in breadcrumb ? (
                <Link
                  aria-label={breadcrumb.text}
                  href={breadcrumb.url}
                  key={breadcrumb.url || breadcrumb.text}
                >
                  {breadcrumb.text}
                </Link>
              ) : (
                <span key={breadcrumb.text}>{breadcrumb.text}</span>
              )
            )}
          </Breadcrumbs>
          <Spacer height={20} />
        </Fragment>
      )}
      <Grid columns="auto max-content">
        <div>
          {title && (
            <Text
              fontWeight={600}
              margin="0px 0px 20px"
              tag="h1"
              type="PageHeader"
            >
              {title}
            </Text>
          )}
          {renderDescription?.() ??
            (description && (
              <Text margin="10px 0px 15px" tag="p">
                {description}
              </Text>
            ))}
        </div>
        {afterHeader}
      </Grid>
      {shouldRenderNonEditableUI && <NonEditableUI />}
      {children}
      {buttonRoute && (
        <Margin margin="20px 0px 9px">
          <ButtonGroup columnSize="minmax(100px, max-content)" justify="end">
            <ZinniaLink
              href={buttonRoute}
              isDisabled={isButtonDisabled}
              variant="primary"
            >
              {buttonText}
            </ZinniaLink>
          </ButtonGroup>
        </Margin>
      )}
      {afterRender}
    </main>
  );
};

ManagerLayout.defaultProps = {
  buttonText: '',
  buttonRoute: '',
  isButtonDisabled: false,
  shouldLimitWidth: false,
};

export default ManagerLayout;
